import styled, { css } from 'styled-components';

import { Button } from '@xing-com/button';
import {
  mediaConfined,
  mediaSmallOrTiny,
  mediaWide,
} from '@xing-com/layout-tokens';
import {
  cornerRadiusM,
  scale010,
  scale020,
  spaceXXS,
  xdlColorBorderStrong,
  xdlColorControlFillTertiary,
  xdlColorFocusRing,
} from '@xing-com/tokens';

import type { VerticalAlignment } from '.';

type CarouselTProps = {
  $hideScrollbar?: boolean;
  $horizontalPadding?: string;
  $horizontalPaddingConfined?: string;
  $horizontalPaddingWide?: string;
  $paddingBottom?: string;
  $paddingBottomConfined?: string;
  $paddingBottomWide?: string;
  $paddingTop?: string;
  $paddingTopConfined?: string;
  $paddingTopWide?: string;
  $verticalAlignment: VerticalAlignment;
};

type ButtonTProps = {
  $paddingBottom: string;
  $paddingBottomConfined: string;
  $paddingBottomWide: string;
  $paddingTop: string;
  $paddingTopConfined?: string;
  $paddingTopWide?: string;
};

// *** Vertical Alignment ***
export const verticalAlignments = {
  stretch: css`
    align-items: stretch;
  `,
  top: css`
    align-items: flex-start;
  `,
  center: css`
    align-items: center;
  `,
  bottom: css`
    align-items: flex-end;
  `,
};

/* Custom scrollbar for Desktop */
const customScrollbar = css`
  --trackColor: ${xdlColorControlFillTertiary};
  --thumbColor: ${xdlColorBorderStrong};

  @media ${mediaWide} {
    scrollbar-color: var(--thumbColor) var(--trackColor);
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      height: ${scale020};
    }

    &::-webkit-scrollbar-track {
      background-color: var(--trackColor);
      border-radius: ${cornerRadiusM};
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--thumbColor);
      border-radius: ${cornerRadiusM};
    }
  }
`;

// *** Components ***
export const Container = styled.div`
  position: relative;

  & > button {
    visibility: hidden;

    @media ${mediaSmallOrTiny} {
      /* completely remove so that viewport is not extended */
      display: none;
    }
  }

  @media ${mediaWide} {
    &:hover > button {
      visibility: visible;
    }

    &:focus-within > button {
      visibility: visible;
    }
  }
`;

export const Carousel = styled.div<CarouselTProps>`
  display: flex;
  flex-wrap: nowrap;
  -webkit-overflow-scrolling: touch;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;

  @media ${mediaWide} {
    scroll-snap-type: x proximity;
  }

  @media (prefers-reduced-motion: reduce) {
    scroll-behavior: auto;
  }

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  ${({ $hideScrollbar }) => {
    if ($hideScrollbar) {
      return css`
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
          height: 0;
          width: 0;
        }
      `;
    } else {
      return css`
        ${customScrollbar};
      `;
    }
  }};

  ${({ $verticalAlignment }) => {
    switch ($verticalAlignment) {
      case 'center':
        return verticalAlignments.center;
      case 'stretch':
        return verticalAlignments.stretch;
      case 'top':
        return verticalAlignments.top;
      case 'bottom':
        return verticalAlignments.bottom;
      default:
        return $verticalAlignment satisfies undefined;
    }
  }};

  ${({ $paddingTop }) => {
    return (
      $paddingTop &&
      css`
        padding-block-start: ${() => $paddingTop};
      `
    );
  }};

  ${({ $paddingTopConfined }) => {
    return (
      $paddingTopConfined &&
      css`
        @media ${mediaConfined} {
          padding-block-start: ${() => $paddingTopConfined};
        }
      `
    );
  }};

  ${({ $paddingTopWide }) => {
    return (
      $paddingTopWide &&
      css`
        @media ${mediaWide} {
          padding-block-start: ${() => $paddingTopWide};
        }
      `
    );
  }};

  ${({ $paddingBottom }) => {
    return (
      $paddingBottom &&
      css`
        padding-block-end: ${() => $paddingBottom};
      `
    );
  }};

  ${({ $paddingBottomConfined }) => {
    return (
      $paddingBottomConfined &&
      css`
        @media ${mediaConfined} {
          padding-block-end: ${() => $paddingBottomConfined};
        }
      `
    );
  }};

  ${({ $paddingBottomWide }) => {
    return (
      $paddingBottomWide &&
      css`
        @media ${mediaWide} {
          padding-block-end: ${() => $paddingBottomWide};
        }
      `
    );
  }};

  ${({ $horizontalPadding }) => {
    return (
      $horizontalPadding &&
      css`
        padding-inline-start: ${() => $horizontalPadding};
      `
    );
  }};

  ${({ $horizontalPaddingConfined }) => {
    return (
      $horizontalPaddingConfined &&
      css`
        @media ${mediaConfined} {
          padding-inline-start: ${() => $horizontalPaddingConfined};
        }
      `
    );
  }};

  ${({ $horizontalPaddingWide }) => {
    return (
      $horizontalPaddingWide &&
      css`
        @media ${mediaWide} {
          padding-inline-start: ${() => $horizontalPaddingWide};
        }
      `
    );
  }};
`;

const pageButton = css<ButtonTProps>`
  && {
    position: absolute;
  }

  ${({ $paddingTop, $paddingBottom }) => {
    if (!$paddingTop || !$paddingBottom) return;
    return css`
      inset-block-start: calc(
        50% - 21px - calc(${() => $paddingBottom} / 2) +
          calc(${() => $paddingTop} / 2)
      );
    `;
  }};

  ${({ $paddingTopConfined, $paddingBottomConfined }) => {
    if (!$paddingTopConfined || !$paddingBottomConfined) return;
    return css`
      @media ${mediaConfined} {
        inset-block-start: calc(
          50% - 21px - calc(${() => $paddingBottomConfined} / 2) +
            calc(${() => $paddingTopConfined} / 2)
        );
      }
    `;
  }};

  ${({ $paddingTopWide, $paddingBottomWide }) => {
    if (!$paddingTopWide || !$paddingBottomWide) return;
    return css`
      @media ${mediaWide} {
        inset-block-start: calc(
          50% - 21px - calc(${() => $paddingBottomWide} / 2) +
            calc(${() => $paddingTopWide} / 2)
        );
      }
    `;
  }};
`;

export const NextPageButton = styled(Button)<ButtonTProps>`
  ${pageButton};
  inset-inline-end: -21px; /* Half medium button */
`;

export const PreviousPageButton = styled(Button)<ButtonTProps>`
  ${pageButton};
  inset-inline-start: -21px; /* Half medium button */
`;
